import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fmtDate, fmtDistanceToNow } from '@/lib/dates'
import { FOLLOW_UP_SENT_STATUSES } from '@dealcloser/shared'
import {
  ArrowLeft,
  Play,
  Pause,
  Users,
  Send,
  MessageSquare,
  Calendar,
  Activity,
  Trash2,
  Pencil,
  UserPlus,
  Settings2,
  Sparkles,
  MessagesSquare,
  X,
  FileText,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useCampaign, useCampaignStats, useCampaignLeads, useActivateCampaign, usePauseCampaign, useRemoveLeadFromCampaign } from './use-campaigns'
import { TestModeButton } from './test-mode-button'
import type { Campaign } from './types'
import { STEP_TYPE_LABELS } from './constants'
import { EditCampaignDialog } from './edit-campaign-dialog'
import { MessageWriterCard } from './message-writer-card'
import { ClaudeWritesBadge } from './campaign-card'
import { AddLeadsDialog } from './add-leads-dialog'
import { EditSequenceDialog } from './edit-sequence-dialog'
import { DeleteCampaignDialog } from './delete-campaign-dialog'
import { PreviewMessagesDialog } from './preview-messages-dialog'
import { CampaignBriefDialog } from './brief/brief-dialog'
import { CampaignRepliesDialog, type RepliesKind } from './campaign-replies-dialog'
import { PlanGate } from '@/components/plan-gate'
import { usePlan } from '@/hooks/use-plan'
import { useUser } from '@clerk/clerk-react'
import { isTahaOnly } from '@/features/admin/staff'

type StatCardProps = {
  readonly label: string
  readonly value: string | number
  readonly icon: React.ReactNode
  readonly sub?: string
  /** When set, the card is clickable and says where it goes. */
  readonly onClick?: () => void
  readonly actionHint?: string
  readonly active?: boolean
}

function StatCard({ label, value, icon, sub, onClick, actionHint, active }: StatCardProps) {
  const body = (
    <CardContent className="pt-4 pb-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold mt-0.5">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          {onClick && actionHint && (
            <p className="mt-1 text-[11px] text-cobalt-bright group-hover:underline">{actionHint}</p>
          )}
        </div>
        <div className="shrink-0 text-muted-foreground">{icon}</div>
      </div>
    </CardContent>
  )
  if (!onClick) return <Card>{body}</Card>
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={[
        'group cursor-pointer transition-colors hover:border-primary/60 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-cobalt-bright bg-primary/10' : '',
      ].join(' ')}
    >
      {body}
    </Card>
  )
}

// Same status sets the server counts with (packages/db queries/campaigns.ts
// getStats), so a filtered table always shows exactly the card's number.
type LeadFilter = 'all' | 'sent' | 'contacted' | 'replied' | 'meetings'
const FILTER_STATUSES: Record<Exclude<LeadFilter, 'all'>, ReadonlySet<string>> = {
  sent: new Set(['connection_sent', 'connected', 'contacted', 'dm_sent', ...FOLLOW_UP_SENT_STATUSES, 'replied', 'qualified', 'meeting_booked', 'won']),
  contacted: new Set(['contacted', 'dm_sent', ...FOLLOW_UP_SENT_STATUSES, 'replied', 'qualified', 'meeting_booked', 'won']),
  replied: new Set(['replied', 'qualified', 'meeting_booked', 'won']),
  meetings: new Set(['meeting_booked', 'won']),
}
const FILTER_LABELS: Record<LeadFilter, string> = {
  all: 'All leads',
  sent: 'Connection sent',
  contacted: 'Messaged',
  replied: 'Replied',
  meetings: 'Meeting booked',
}

function StatusBadge({ status }: { readonly status: Campaign['status'] }) {
  const config: Record<Campaign['status'], { label: string; className: string }> = {
    active: {
      label: 'Active',
      className: 'border-transparent bg-emerald-500/12 text-emerald-300 ',
    },
    paused: {
      label: 'Paused',
      className: 'border-transparent bg-amber-500/12 text-amber-300 ',
    },
    draft: {
      label: 'Draft',
      className: 'border-transparent bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)] dark:text-[var(--color-silver)]',
    },
    completed: {
      label: 'Completed',
      className: 'border-transparent bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] ',
    },
    deleted: {
      label: 'Deleted',
      className: 'border-transparent bg-red-500/12 text-red-300 ',
    },
  }
  const { label, className } = config[status]
  return <Badge className={className}>{label}</Badge>
}

type CampaignDetailProps = {
  readonly campaignId: string
  readonly onBack: () => void
}

export function CampaignDetail({ campaignId, onBack }: CampaignDetailProps) {
  const { data: campaign, isLoading, error } = useCampaign(campaignId)
  const { data: stats } = useCampaignStats(campaignId)
  const { data: campaignLeadsData } = useCampaignLeads(campaignId)
  const campaignLeads = campaignLeadsData?.leads ?? []
  const campaignLeadsTotal = campaignLeadsData?.total ?? campaignLeads.length
  const activate = useActivateCampaign()
  const pause = usePauseCampaign()
  const removeLead = useRemoveLeadFromCampaign()
  const [editOpen, setEditOpen] = useState(false)
  const [addLeadsOpen, setAddLeadsOpen] = useState(false)
  const [editSequenceOpen, setEditSequenceOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [briefOpen, setBriefOpen] = useState(false)
  // Stat-card click-throughs (owner request 2026-09-22).
  const [leadFilter, setLeadFilter] = useState<LeadFilter>('all')
  const [repliesKind, setRepliesKind] = useState<RepliesKind | null>(null)
  const leadsTableRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  function showLeads(filter: LeadFilter) {
    setLeadFilter(filter)
    requestAnimationFrame(() => leadsTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }
  // Plan tier — used by PlanGate around feature-tier UI.
  const plan = usePlan()
  // Taha-only gate for internal debug affordances. As of 2026-06-06
  // (pre-launch), Preview AI Messages and Test Mode are hidden from EVERYONE
  // except Taha — even Eli/TJ in the staff allowlist. They're powerful
  // debug tools that confuse new customers (Preview spends LLM credits on
  // demand; Test Mode bypasses activity windows and step delays). Taha keeps
  // them visible for live customer-support investigations during launch.
  const { user: clerkUser } = useUser()
  const isInternalAdmin = isTahaOnly(clerkUser?.id)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="h-12 w-full max-w-64 rounded-lg bg-muted/30 animate-pulse" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-muted/30 animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <p className="text-lg font-medium">Campaign not found</p>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to campaigns
        </Button>
      </div>
    )
  }

  const replyRate =
    campaign.sent_count > 0
      ? ((campaign.replied_count / campaign.sent_count) * 100).toFixed(1)
      : '0.0'

  const filteredLeads =
    leadFilter === 'all' ? campaignLeads : campaignLeads.filter((l) => FILTER_STATUSES[leadFilter].has(l.status))

  const meetingRate =
    campaign.replied_count > 0
      ? ((campaign.meeting_count / campaign.replied_count) * 100).toFixed(1)
      : '0.0'

  const healthColor =
    stats?.account_health === 'good'
      ? 'bg-green-500'
      : stats?.account_health === 'warning'
        ? 'bg-yellow-500'
        : 'bg-red-500'


  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="self-start -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to campaigns
        </Button>

        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between xl:gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="break-words text-2xl font-bold">{campaign.name}</h2>
              <StatusBadge status={campaign.status} />
              <ClaudeWritesBadge campaign={campaign} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Created {fmtDate(campaign.created_at, 'MMM d, yyyy')} &middot;{' '}
              Updated {fmtDistanceToNow(campaign.updated_at, { addSuffix: true })}
            </p>
          </div>

          {campaign.status !== 'deleted' && (
            <div className="flex flex-col items-start gap-1 xl:shrink-0 xl:items-end">
              <div className="flex flex-wrap items-center gap-2 xl:justify-end">
              {isInternalAdmin && (
                <PlanGate requiredPlan="pro" currentPlan={plan}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewOpen(true)}
                    title="See sample AI messages for this campaign's leads (staff only)"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Preview AI Messages
                  </Button>
                </PlanGate>
              )}
              {isInternalAdmin && <TestModeButton campaign={campaign} />}
              <Button variant="outline" size="sm" onClick={() => setBriefOpen(true)}>
                <FileText className="h-4 w-4 mr-1" />
                Messaging brief
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              {campaign.status === 'active' ? (
                <Button
                  variant="outline"
                  onClick={() => pause.mutate(campaign.id)}
                  disabled={pause.isPending}
                >
                  <Pause className="h-4 w-4 mr-1" />
                  {pause.isPending ? 'Pausing...' : 'Pause Campaign'}
                </Button>
              ) : campaign.status !== 'completed' ? (
                <Button
                  onClick={() => activate.mutate(campaign.id)}
                  disabled={activate.isPending}
                >
                  <Play className="h-4 w-4 mr-1" />
                  {activate.isPending ? 'Activating...' : 'Activate Campaign'}
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
              </div>
              {activate.isError && (
                <p className="max-w-md text-xs xl:text-right text-destructive">
                  {activate.error instanceof Error ? activate.error.message : 'Could not activate this campaign.'}
                </p>
              )}
              {campaign.brief_required && !campaign.brief_confirmed_at && (
                <p className="max-w-md text-xs xl:text-right font-medium text-amber-600 dark:text-amber-400">
                  This campaign can&apos;t send until you confirm its{' '}
                  <button type="button" className="underline underline-offset-2" onClick={() => setBriefOpen(true)}>
                    messaging brief
                  </button>
                  .
                </p>
              )}
              {isInternalAdmin && campaign.test_mode && (
                <p className="max-w-md text-xs font-medium text-amber-300 xl:text-right">
                  Test Mode auto-disables in the time shown on the button. Activity window
                  + step delays bypassed; per-lead retry, daily limit, and 3-action-per-tick
                  cap still apply.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] sm:gap-4">
        <StatCard
          label="Total Leads"
          value={campaign.leads_count}
          icon={<Users className="h-5 w-5" />}
          onClick={() => showLeads('all')}
          actionHint="View leads"
          active={leadFilter === 'all' && repliesKind === null}
        />
        <StatCard
          label="Connections Sent"
          value={campaign.sent_count}
          icon={<UserPlus className="h-5 w-5" />}
          onClick={() => showLeads('sent')}
          actionHint="View leads"
          active={leadFilter === 'sent'}
        />
        <StatCard
          label="Leads Messaged"
          value={campaign.contacted_count}
          icon={<Send className="h-5 w-5" />}
          onClick={() => showLeads('contacted')}
          actionHint="View leads"
          active={leadFilter === 'contacted'}
        />
        <StatCard
          label="Replies"
          value={campaign.replied_count}
          icon={<MessageSquare className="h-5 w-5" />}
          onClick={() => setRepliesKind('replied')}
          actionHint="Read replies"
        />
        <StatCard
          label="Reply Rate"
          value={`${replyRate}%`}
          icon={<Activity className="h-5 w-5" />}
          onClick={() => setRepliesKind('replied')}
          actionHint="Read replies"
        />
        <StatCard
          label="Meetings"
          value={campaign.meeting_count}
          icon={<Calendar className="h-5 w-5" />}
          onClick={() => setRepliesKind('meetings')}
          actionHint="View meetings"
        />
        <StatCard
          label="Meeting Rate"
          value={`${meetingRate}%`}
          icon={<Activity className="h-5 w-5" />}
          onClick={() => setRepliesKind('meetings')}
          actionHint="View meetings"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Safety gauge */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Daily Activity</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {/* Daily volume is platform-managed; the per-campaign limit is
                deliberately not shown (2026-07-27 owner directive). */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Messages today</span>
              <span className="font-medium">{stats?.messages_today ?? 0}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Sends go out weekdays between 9:00 AM and 5:00 PM New York time.
              Weekend sending can be switched on in Settings, Safety.
            </p>

            <div className="flex items-center justify-between text-sm pt-1">
              <span className="text-muted-foreground">Account health</span>
              <div className="flex items-center gap-1.5">
                <div className={`h-2 w-2 rounded-full ${healthColor}`} />
                <span className="font-medium capitalize">
                  {stats?.account_health ?? 'good'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sequence visualization */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Sequence</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditSequenceOpen(true)}>
                <Settings2 className="h-3.5 w-3.5 mr-1" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-0">
              {campaign.sequence_config.steps.map((step, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex flex-col items-center">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-cobalt-bright">
                      {index + 1}
                    </div>
                    {index < campaign.sequence_config.steps.length - 1 && (
                      <div className="w-px h-5 bg-border" />
                    )}
                  </div>
                  <div className="pb-2 pt-0.5">
                    <p className="text-sm font-medium">
                      {STEP_TYPE_LABELS[step.type] ?? step.type}
                    </p>
                    {step.delay_days > 0 && (
                      <p className="text-xs text-muted-foreground">
                        +{step.delay_days} days
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <MessageWriterCard campaign={campaign} />

      {/* Leads table */}
      <Card ref={leadsTableRef} className="scroll-mt-4">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-sm font-semibold">
                Leads ({leadFilter === 'all' ? campaignLeadsTotal : filteredLeads.length})
              </CardTitle>
              {leadFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1 text-xs">
                  {FILTER_LABELS[leadFilter]}
                  <button
                    type="button"
                    aria-label="Clear filter"
                    className="ml-0.5 rounded hover:text-foreground"
                    onClick={() => setLeadFilter('all')}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setAddLeadsOpen(true)}>
              <UserPlus className="h-3.5 w-3.5 mr-1" />
              Add Leads
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredLeads.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {leadFilter === 'all' ? 'No leads found for this campaign.' : `No leads in "${FILTER_LABELS[leadFilter]}" yet.`}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Messages</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => {
                  const displayName =
                    lead.full_name ?? `${lead.first_name} ${lead.last_name}`
                  const lastActivity = fmtDistanceToNow(lead.last_activity_at, { addSuffix: true })

                  return (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <div>
                          {lead.linkedin_url ? (
                            <a
                              href={lead.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-sm hover:underline hover:text-cobalt-bright"
                            >
                              {displayName}
                            </a>
                          ) : (
                            <p className="font-medium text-sm">{displayName}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {lead.job_title ?? ''}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{lead.company ?? '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">
                          {lead.status.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {lead.messages_sent ?? 0}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {lastActivity}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {FILTER_STATUSES.replied.has(lead.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-cobalt-bright"
                            title="Open conversation"
                            aria-label="Open conversation"
                            onClick={() => navigate(`/inbox?lead=${encodeURIComponent(lead.id)}`)}
                          >
                            <MessagesSquare className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeLead.mutate({ campaignId: campaign.id, leadId: lead.id })}
                          disabled={removeLead.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
          {campaignLeads.length > 0 && campaignLeads.length < campaignLeadsTotal && (
            <div className="py-2 text-center text-xs text-muted-foreground border-t">
              Showing first {campaignLeads.length} of {campaignLeadsTotal} leads
            </div>
          )}
        </CardContent>
      </Card>

      <EditCampaignDialog campaign={campaign} open={editOpen} onOpenChange={setEditOpen} />
      <CampaignBriefDialog
        campaignId={campaign.id}
        campaignName={campaign.name}
        open={briefOpen}
        onOpenChange={setBriefOpen}
      />
      <AddLeadsDialog campaignId={campaign.id} open={addLeadsOpen} onOpenChange={setAddLeadsOpen} />
      <EditSequenceDialog
        campaignId={campaign.id}
        currentSequence={campaign.sequence_config}
        open={editSequenceOpen}
        onOpenChange={setEditSequenceOpen}
      />
      <DeleteCampaignDialog
        campaignName={campaign.name}
        campaignId={campaign.id}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={onBack}
      />
      <CampaignRepliesDialog
        campaignId={campaign.id}
        kind={repliesKind ?? 'replied'}
        open={repliesKind !== null}
        onOpenChange={(o) => {
          if (!o) setRepliesKind(null)
        }}
      />
      <PreviewMessagesDialog
        campaignId={campaign.id}
        campaignName={campaign.name}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />
    </div>
  )
}
